// src\components\stripe\StripePayment.js
// eslint-disable-next-line no-unused-vars
import React, {useState, useEffect} from 'react';
import {loadStripe} from '@stripe/stripe-js';
import {Elements, useStripe, useElements, PaymentElement} from '@stripe/react-stripe-js';
import api from '../../api/axios';
import styled from 'styled-components';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_API);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  padding: 20px;
  color: white;
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 10px;
  max-width: 100%;

  @media (max-width: 991px) {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  }

  @media (max-width: 440px) {
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  }

  @media (max-width: 380px) {
    grid-template-columns: repeat(auto-fit, minmax(90px, 1fr));
  }
`;

const ProductCard = styled.div`
  background-image: url(/assets/ui/container-bg.png);
  background-repeat: no-repeat;
  background-position: center center;
  background-size: 100% 100%;
  border-radius: 10px;
  padding: 15px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  border: ${({selected}) => (selected ? '2px solid #ffbb00' : '2px solid transparent')};

  &:hover {
    transform: scale(1.05);
    border-color: #ffbb00;
  }

  img {
    width: 70px;
    height: 70px;
    margin-bottom: 10px;
  }

  h4 {
    margin: 5px 0;
    font-size: 16px;
  }

  p {
    font-size: 14px;
    color: #bbb;
  }
`;

const StyledButton = styled.button`
  color: white;
  font-size: 16px;
  padding: 12px 20px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: 0.3s;
  width: 100%;
  &:disabled,
&:disabled:hover {
  color: gray;
  opacity: 0.3;
  cursor: not-allowed;
  background: #444;
}
`;

const PaymentContainer = styled.div`
  max-width: 450px;
  margin: 50px auto;
  background-image: url(/assets/ui/container-bg.png);
  background-repeat: no-repeat;
  background-position: center center;
  background-size: 100% 100%;
  padding: 25px;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0px 6px 15px rgba(0, 0, 0, 0.4);
`;

// Spinner-Komponente
const Spinner = styled.div`
  border: 4px solid #f3f3f3;
  border-top: 4px solid #007bff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const BonusText = styled.p`
  color: gold;
  font-weight: bold;
  text-align: center;
`;

const StyledCheckBox = styled.input`
  height: 20px;
  width: 20px;
  margin-right: 10px;
`;

const RefundPolicyContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 10px 0;
  width: 100%;
  font-size: 16px;
  color: #ccc;

  a {
    color: #ffbb00;
    text-decoration: underline;
    font-size: 16px;
  }
`;

const CheckoutForm = ({clientSecret}) => {
  const stripe = useStripe();
  const elements = useElements();
  const {t} = useTranslation();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const handleSubmit = async event => {
    event.preventDefault();
    setLoading(true);

    if (!stripe || !elements) {
      setMessage(t('checkoutForm.paymentNotStarted'));
      setLoading(false);
      return;
    }

    try {
      // Direkte Bestätigung der Zahlung über confirmPayment (KEIN createPaymentMethod!)
      const {error} = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: 'https://arcane-rappelz.com/payment-success',
        },
      });

      if (error) {
        setMessage(t('checkoutForm.errorPrefix') + error.message);
      } else {
        window.location.href = 'https://arcane-rappelz.com/payment-success';
      }
    } catch (err) {
      setMessage(t('checkoutForm.errorPrefix') + err.message);
    }

    setLoading(false);
  };

  return (
    <PaymentContainer>
      <h2>{t('checkoutForm.securePayment')}</h2>
      <form onSubmit={handleSubmit}>
        <PaymentElement />
        <StyledButton type="submit" disabled={!stripe || loading}>
          {loading ? t('checkoutForm.processingPayment') : t('checkoutForm.payNow')}
        </StyledButton>
        {message && <p>{message}</p>}
      </form>
    </PaymentContainer>
  );
};

const StripePayment = () => {
  const {t} = useTranslation();
  const [clientSecret, setClientSecret] = useState('');
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isProductsLoading, setProductsLoading] = useState(true);
  const [bonus, setBonus] = useState(null);
  const [refundPolicyAccepted, setRefundPolicyAccepted] = useState(false);

  // Produkte und Bonus-Status laden
  useEffect(() => {


    api
      .get('payment/get-stripe-products.php')
      .then(response => {
        setProducts(response.data);
        setSelectedProduct(response.data[0]); // Erstes Produkt auswählen
        setProductsLoading(false);
      })
      .catch(error => {
        setProductsLoading(false);
      });

      api
      .get('payment/bonus-status.php')
      .then(response => {
        if (response.data.active) {
          setBonus(response.data);
        }
      })
      .catch(error => {});
  }, []);

  // Hilfsfunktion zur Berechnung der bonusierten Arcanium-Anzahl
  const getBonusArcanium = product => {
    if (!product || !bonus) return "0"; // ✅ Falls noch nicht geladen, abbrechen
    return Math.round(product.package_size + (product.package_size * (bonus.bonus_percentage / 100))).toLocaleString('de-DE');
  };

  const handlePayment = async () => {
    if (!selectedProduct) {
      //console.error("❌ Kein Produkt ausgewählt!");
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('payment/create-payment-intent.php', {
        product_id: selectedProduct.product_id,
        amount: selectedProduct.price,
        package_size: selectedProduct.package_size, // Diese Zahl bleibt für die Zahlung gleich – Backend kann den Bonus separat verarbeiten
      });

      if (!response.data.success) {
        throw new Error(response.data.error || t('stripePayment.errorCreatingIntent'));
      }

      setClientSecret(response.data.client_secret);
    } catch (err) {
      throw new Error(err || t('stripePayment.paymentFailed'));
    }
    setLoading(false);
  };

  const handleCancel = () => {
    setClientSecret('');
  };

  return (
    <Container>
      {!clientSecret ? (
        <>
          <h2>{t('stripePayment.choosePackage')}</h2>
          {/* Bonus-Hinweis */}
          {bonus && (
            <BonusText>
              {t('stripePayment.bonusText', {
                name: bonus.name,
                bonusPercentage: bonus.bonus_percentage,
                endDate: bonus.end_date,
              })}
            </BonusText>
          )}
          {isProductsLoading ? (
            <Spinner />
          ) : (
            <ProductGrid>
              {products.map(product => (
                <ProductCard
                  key={product.product_id}
                  selected={selectedProduct && selectedProduct.product_id === product.product_id}
                  onClick={() => setSelectedProduct(product)}
                >
                  <img src={product.icon} alt={product.label} />
                  {bonus ? (
                    <>
                      <h4 style={{textDecoration: 'line-through', margin: '5px 0'}}>
                        {product.package_size.toLocaleString('de-DE')} Arcanium
                      </h4>
                      <h4 style={{margin: '5px 0'}}>{getBonusArcanium(product)} Arcanium</h4>
                    </>
                  ) : (
                    <h4 style={{margin: '5px 0'}}>{product.package_size.toLocaleString('de-DE')} Arcanium</h4>
                  )}
                  <p>{(product.price / 100).toFixed(2)} €</p>
                </ProductCard>
              ))}
            </ProductGrid>
          )}
          <RefundPolicyContainer>
            <label>
              <StyledCheckBox
                type="checkbox"
                checked={refundPolicyAccepted}
                onChange={e => setRefundPolicyAccepted(e.target.checked === true)}
              />
              {t('stripePayment.refundPolicy')} <Link to="/refund-policy">{t('stripePayment.refundPolicyLink')}</Link>.
            </label>
          </RefundPolicyContainer>
          <StyledButton onClick={handlePayment} disabled={loading || !selectedProduct || !refundPolicyAccepted}>
            {loading
              ? t('stripePayment.loadingStripe')
              : bonus
              ? t('stripePayment.buyButton', {arcanium: getBonusArcanium(selectedProduct)})
              : t('stripePayment.buyButton', {
                  arcanium: selectedProduct ? selectedProduct.package_size.toLocaleString('de-DE') : '',
                })}
          </StyledButton>
        </>
      ) : (
        <>
          {/* Checkout-Ansicht */}
          <h2>{t('stripePayment.checkoutTitle')}</h2>
          {bonus ? (
            <>
              <p style={{textDecoration: 'line-through', margin: 0}}>{selectedProduct.package_size} Arcanium</p>
              <p style={{margin: 0}}>{getBonusArcanium(selectedProduct)} Arcanium</p>
            </>
          ) : (
            <p>{t('stripePayment.selectedProduct', {label: selectedProduct.label})}</p>
          )}
          <StyledButton onClick={handleCancel}>{t('stripePayment.back')}</StyledButton>
          <Elements stripe={stripePromise} options={{clientSecret, appearance: {theme: 'stripe', locale: 'en'}}}>
            <CheckoutForm clientSecret={clientSecret} />
          </Elements>
        </>
      )}
    </Container>
  );
};

export default StripePayment;
